/**
 * Workflow builder — trigger segment plus the step sequence.
 *
 * Steps are held as plain objects and the whole list is re-rendered after every
 * change. Conditions nest (`yes` / `no` branches contain steps of their own), so
 * both the rendering and the "insert here" addressing are recursive: a step is
 * addressed by its path through the tree, e.g. `2.yes.0`.
 */
define('mailingWorkflowDetailView',[
  'module',
  'app',
  'underscore',
  'jquery',
  'marionette',
  'mailingShell',
  'entities/mailing'
], function (
  module,
  App,
  _,
  $,
  Marionette,
  Shell,
  MailingApi
) {
  'use strict';

  var CHECKS = [
    { key: 'opened_last_email', label: 'opened the previous email' },
    { key: 'clicked_last_email', label: 'clicked the previous email' },
    { key: 'field', label: 'a contact field matches…' }
  ];

  // Step JSON keys are fixed by the backend runner's format (snake_case), so
  // they are addressed through constants rather than dot access.
  var KEY_TEMPLATE = 'template_id';
  var KEY_SUBJECT = 'subject_override';

  var FIELD_OPERATORS = [
    { key: 'eq', label: 'is' },
    { key: 'neq', label: 'is not' },
    { key: 'contains', label: 'contains' },
    { key: 'not_contains', label: 'does not contain' },
    { key: 'gt', label: '>' },
    { key: 'lt', label: '<' }
  ];

  var WorkflowDetailView = Marionette.ItemView.extend({

    className: 'mailing-page mailing-workflow-detail',

    template: _.template(
      '<%= nav %>' +
      '<div class="mlg-body"><div class="mlg-loading">Loading workflow…</div></div>'
    ),

    ui: {
      body: '.mlg-body'
    },

    events: {
      'click .mlg-save-workflow': 'onSave',
      'click .mlg-back': 'onBack',
      'click .mlg-add-step': 'onAddStep',
      'click .mlg-remove-step': 'onRemoveStep',
      'click .mlg-move-up': 'onMoveUp',
      'click .mlg-move-down': 'onMoveDown',
      'change .f-segment': 'onDirty',
      'change .s-input': 'onStepInput',
      'change .s-check': 'onCheckChanged',
      'keyup .f-name': 'onDirty'
    },

    initialize: function (options) {
      this.secId = options.secId;
      this.steps = [];
    },

    serializeData: function () {
      return {
        nav: Shell.navHtml('workflows',
          '<button type="button" class="mlg-btn primary mlg-save-workflow">Save</button>' +
          '<a class="mlg-btn mlg-back" href="#mailing/workflows">Back to list</a>')
      };
    },

    onShow: function () {
      var self = this;
      $.when(MailingApi.workflow(this.secId), MailingApi.segments(), MailingApi.templates())
        .done(function (workflow, segments, templates) {
          self.workflow = workflow;
          self.segments = segments || [];
          self.templates = templates || [];
          self.steps = self.parseSteps(workflow.steps);
          self.renderBuilder();
          self.loadEnrollments();
        })
        .fail(function (xhr) {
          self.ui.body.html('<div class="mlg-error">' + _.escape(MailingApi.errorMessage(xhr)) + '</div>');
        });
    },

    parseSteps: function (raw) {
      try {
        var parsed = JSON.parse(raw || '[]');
        return _.isArray(parsed) ? parsed : [];
      } catch (error) {
        return [];
      }
    },

    renderBuilder: function () {
      var segmentOptions = '<option value=""></option>' + _.map(this.segments, function (segment) {
        return '<option value="' + segment.secId + '"' +
          (segment.secId === this.workflow.segmentSecId ? ' selected' : '') + '>' +
          _.escape(segment.name) + ' (' + Shell.formatNumber(segment.subscriberCount) + ')</option>';
      }, this).join('');

      this.ui.body.html(
        '<div class="mlg-wf-head">' +
        '  <label class="inline grow">Name<input type="text" class="f-name" value="' +
             _.escape(this.workflow.name || '') + '"></label>' +
        '  <label class="inline grow">Trigger segment<select class="f-segment">' +
             segmentOptions + '</select></label>' +
        '  <span class="mlg-chip st-' + (this.workflow.status || 'DRAFT').toLowerCase() + '">' +
             Shell.prettyCase(this.workflow.status) + '</span>' +
        '</div>' +
        '<div class="mlg-hint">Every active contact of the segment is enrolled once and then walks the ' +
          'steps below. Editing the steps does not re-route contacts already inside.</div>' +
        '<div class="mlg-steps"></div>' +
        '<h3 class="mlg-section">Recent enrolments</h3>' +
        '<div class="mlg-enrollments"><div class="mlg-loading">Loading…</div></div>');

      this.renderSteps();
    },

    renderSteps: function () {
      this.ui.body.find('.mlg-steps').html(this.branchHtml(this.steps, '', 0));
    },

    /**
     * Renders one branch. `path` is the dotted address of the branch's parent
     * ('' at the root), used by the buttons to locate the step they act on.
     */
    branchHtml: function (steps, path, depth) {
      var self = this;
      var items = _.map(steps, function (step, index) {
        return self.stepHtml(step, path === '' ? String(index) : path + '.' + index, depth);
      }).join('');

      return '<div class="mlg-branch" data-path="' + path + '" data-depth="' + depth + '">' +
        items +
        '<div class="mlg-add-bar">' +
        '  <button type="button" class="mlg-btn small mlg-add-step" data-type="wait">+ Wait</button>' +
        '  <button type="button" class="mlg-btn small mlg-add-step" data-type="email">+ Email</button>' +
        (depth < 2 ? '  <button type="button" class="mlg-btn small mlg-add-step" data-type="condition">+ Condition</button>' : '') +
        '</div>' +
        '</div>';
    },

    stepHtml: function (step, path, depth) {
      var header =
        '<div class="s-head">' +
        '  <span class="s-type t-' + _.escape(step.type || '') + '">' +
             Shell.prettyCase(step.type) + '</span>' +
        '  <span class="s-tools">' +
        '    <button type="button" class="mlg-icon-btn mlg-move-up" title="Move up">↑</button>' +
        '    <button type="button" class="mlg-icon-btn mlg-move-down" title="Move down">↓</button>' +
        '    <button type="button" class="mlg-icon-btn mlg-remove-step" title="Remove">×</button>' +
        '  </span>' +
        '</div>';

      var body = '';
      if (step.type === 'wait') {
        var units = ['hours', 'days', 'weeks'];
        body = '<div class="s-body">Wait ' +
          '<input type="number" min="1" class="s-input narrow" data-key="duration" value="' +
            (step.duration || 1) + '"> ' +
          '<select class="s-input" data-key="unit">' + _.map(units, function (unit) {
            return '<option value="' + unit + '"' + (unit === step.unit ? ' selected' : '') + '>' +
              unit + '</option>';
          }).join('') + '</select></div>';
      } else if (step.type === 'email') {
        var templateOptions = '<option value=""></option>' + _.map(this.templates, function (template) {
          return '<option value="' + template.secId + '"' +
            (template.secId === step[KEY_TEMPLATE] ? ' selected' : '') + '>' +
            _.escape(template.name) + '</option>';
        }).join('');
        body = '<div class="s-body">' +
          '<label class="inline">Template<select class="s-input" data-key="template_id">' +
            templateOptions + '</select></label>' +
          '<label class="inline grow">Subject override<input type="text" class="s-input" ' +
            'data-key="subject_override" value="' + _.escape(step[KEY_SUBJECT] || '') +
            '" placeholder="(use the template subject)"></label>' +
          '</div>';
      } else if (step.type === 'condition') {
        body = '<div class="s-body">' +
          '<label class="inline">If the contact <select class="s-input s-check" data-key="check">' +
            _.map(CHECKS, function (check) {
              return '<option value="' + check.key + '"' +
                (check.key === step.check ? ' selected' : '') + '>' + _.escape(check.label) + '</option>';
            }).join('') + '</select></label>' +
          (step.check === 'field' ? this.fieldConditionHtml(step) : '') +
          '</div>' +
          '<div class="s-branches">' +
          '  <div class="s-branch yes"><div class="b-label">Yes</div>' +
               this.branchHtml(step.yes || [], path + '.yes', depth + 1) + '</div>' +
          '  <div class="s-branch no"><div class="b-label">No</div>' +
               this.branchHtml(step.no || [], path + '.no', depth + 1) + '</div>' +
          '</div>';
      }

      return '<div class="mlg-step s-' + _.escape(step.type || '') + '" data-path="' + path + '">' +
        header + body + '</div>';
    },

    fieldConditionHtml: function (step) {
      return '<span class="s-field-cond">' +
        '<input type="text" class="s-input narrow" data-key="field" placeholder="field" value="' +
          _.escape(step.field || '') + '">' +
        '<select class="s-input" data-key="operator">' + _.map(FIELD_OPERATORS, function (operator) {
          return '<option value="' + operator.key + '"' +
            (operator.key === step.operator ? ' selected' : '') + '>' + _.escape(operator.label) + '</option>';
        }).join('') + '</select>' +
        '<input type="text" class="s-input" data-key="value" placeholder="value" value="' +
          _.escape(step.value || '') + '">' +
        '</span>';
    },

    //----------Step tree addressing-----------------------------------------//

    /** Resolves a dotted path to { list, index } so callers can splice in place. */
    locate: function (path) {
      var parts = String(path).split('.');
      var list = this.steps;
      for (var i = 0; i < parts.length - 1; i += 2) {
        var index = Number(parts[i]);
        var branch = parts[i + 1];
        if (branch === undefined) { break; }
        var step = list[index];
        if (!step[branch]) { step[branch] = []; }
        list = step[branch];
      }
      return { list: list, index: Number(parts[parts.length - 1]) };
    },

    /** Resolves the branch a path points *into* (used by the add bar). */
    locateBranch: function (path) {
      if (path === '') { return this.steps; }
      var parts = String(path).split('.');
      var list = this.steps;
      for (var i = 0; i < parts.length; i += 2) {
        var step = list[Number(parts[i])];
        var branch = parts[i + 1];
        if (!step[branch]) { step[branch] = []; }
        list = step[branch];
      }
      return list;
    },

    onAddStep: function (e) {
      e.stopPropagation();
      var $button = $(e.currentTarget);
      var path = $button.closest('.mlg-branch').data('path');
      var list = this.locateBranch(path === undefined ? '' : String(path));
      var type = $button.data('type');

      var step = { id: 's' + Date.now() + Math.floor(Math.random() * 1000), type: type };
      if (type === 'wait') {
        step.duration = 1;
        step.unit = 'days';
      } else if (type === 'email') {
        step[KEY_TEMPLATE] = '';
      } else if (type === 'condition') {
        step.check = 'opened_last_email';
        step.yes = [];
        step.no = [];
      }
      list.push(step);
      this.renderSteps();
    },

    onRemoveStep: function (e) {
      e.stopPropagation();
      var located = this.locate($(e.currentTarget).closest('.mlg-step').data('path'));
      located.list.splice(located.index, 1);
      this.renderSteps();
    },

    onMoveUp: function (e) {
      e.stopPropagation();
      this.move($(e.currentTarget).closest('.mlg-step').data('path'), -1);
    },

    onMoveDown: function (e) {
      e.stopPropagation();
      this.move($(e.currentTarget).closest('.mlg-step').data('path'), 1);
    },

    move: function (path, offset) {
      var located = this.locate(path);
      var target = located.index + offset;
      if (target < 0 || target >= located.list.length) { return; }
      var step = located.list.splice(located.index, 1)[0];
      located.list.splice(target, 0, step);
      this.renderSteps();
    },

    onStepInput: function (e) {
      var $input = $(e.currentTarget);
      var located = this.locate($input.closest('.mlg-step').data('path'));
      var step = located.list[located.index];
      var key = $input.data('key');
      step[key] = $input.attr('type') === 'number' ? Number($input.val()) : $input.val();
    },

    /** Switching the check swaps the whole condition body, so re-render. */
    onCheckChanged: function (e) {
      this.onStepInput(e);
      this.renderSteps();
    },

    onDirty: function () {
      // Name and segment are read straight off the DOM on save; nothing to do
      // here beyond letting the change event bubble past the step handlers.
    },

    //----------Enrolments---------------------------------------------------//

    loadEnrollments: function () {
      var self = this;
      MailingApi.workflowEnrollments(this.secId).done(function (enrollments) {
        var $host = self.ui.body.find('.mlg-enrollments');
        if (!enrollments.length) {
          $host.html(Shell.emptyHtml('Nobody is enrolled yet.'));
          return;
        }
        $host.html('<table class="mlg-table compact">' +
          '<thead><tr><th>Contact</th><th>Status</th><th>Steps left</th>' +
          '<th>Next step</th><th>Enrolled</th></tr></thead><tbody>' +
          _.map(enrollments, function (enrollment) {
            var name = ((enrollment.firstName || '') + ' ' + (enrollment.lastName || '')).trim();
            return '<tr>' +
              '<td>' + _.escape(name || enrollment.email || '') +
                '<div class="c-sub">' + _.escape(enrollment.email || '') + '</div></td>' +
              '<td><span class="mlg-chip st-' + (enrollment.status || '').toLowerCase() + '">' +
                Shell.prettyCase(enrollment.status) + '</span></td>' +
              '<td class="num">' + enrollment.remainingSteps + '</td>' +
              '<td>' + Shell.formatDate(enrollment.nextRunAt, true) + '</td>' +
              '<td>' + Shell.formatDate(enrollment.enrolledAt, true) + '</td>' +
              '</tr>';
          }).join('') + '</tbody></table>');
      });
    },

    //----------Save---------------------------------------------------------//

    onSave: function () {
      var self = this;
      var invalid = this.firstInvalidStep(this.steps);
      if (invalid) {
        Shell.banner(this.$el, invalid, 'error');
        return;
      }
      MailingApi.updateWorkflow(this.secId, {
        name: this.ui.body.find('.f-name').val(),
        segmentSecId: this.ui.body.find('.f-segment').val(),
        steps: JSON.stringify(this.steps)
      }).done(function (workflow) {
        self.workflow = workflow;
        Shell.banner(self.$el, 'Workflow saved', 'ok');
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    /** Catches the mistake the runner would otherwise skip silently at 3am. */
    firstInvalidStep: function (steps) {
      for (var i = 0; i < steps.length; i++) {
        var step = steps[i];
        if (step.type === 'email' && !step[KEY_TEMPLATE]) {
          return 'An email step has no template selected';
        }
        if (step.type === 'condition') {
          if (step.check === 'field' && !step.field) {
            return 'A condition on a contact field has no field name';
          }
          var nested = this.firstInvalidStep(step.yes || []) || this.firstInvalidStep(step.no || []);
          if (nested) { return nested; }
        }
      }
      return null;
    },

    onBack: function (e) {
      e.preventDefault();
      App.navigate('mailing/workflows', { trigger: true });
    }
  });

  module.exports = WorkflowDetailView;
});

